import { css } from '@emotion/core'
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { hasValidChildren } from '../hasValidChildren'
import useProcessedHeights from './useProcessedHeights'

const centerCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
`

type CompareType = `compare${number}`
type HeightById = Record<Exclude<CompareType, 'resultant'>, number>
type Heights = Partial<Record<CompareType, HeightById>>
type ObserveProps = {
  /** Captured from the ref at effect time: on unmount cleanup ref.current is already null. */
  element: HTMLDivElement
  id: string
  skipDelay?: boolean
}
type HeightContextValue = {
  maxHeights: HeightById
  observe: (props: ObserveProps) => void
  unobserve: (props: ObserveProps) => void
}

const HeightContext = createContext<HeightContextValue | null>(null)

type HeightDivProps = {
  type: CompareType
  id: string
  children?: ReactNode
  centerContents?: boolean
  additionalHeight?: number
  /** When to skip the 50ms debounce delay for this HeightDiv. Use when you have expanders or animations that change height continuously. */
  skipDelay?: boolean
  'data-testid'?: string
}

const useHeightContext = (): HeightContextValue => {
  const context = useContext(HeightContext)
  if (!context) throw new Error('useHeightContext needs a HeightProvider as a parent.')
  return context
}

/** To get compare types heights and use their max height for the types per id. */
export const HeightDiv = ({
  type,
  id,
  children,
  centerContents,
  additionalHeight = 0,
  skipDelay = false,
  'data-testid': dataTestId,
}: HeightDivProps): JSX.Element | null => {
  const ref = useRef<HTMLDivElement>(null)
  const { maxHeights, observe, unobserve } = useHeightContext()

  useEffect(() => {
    const element = ref.current
    if (!element) return undefined
    observe({ element, id, skipDelay })
    return () => {
      unobserve({ element, id, skipDelay })
    }
  }, [type, id, observe, unobserve, skipDelay])

  const maxHeight: number | undefined = maxHeights[id]

  if (!hasValidChildren(children) && maxHeight === 0) return null

  return (
    <div
      style={maxHeight ? { minHeight: maxHeight } : {}}
      css={centerContents ? centerCss : undefined}
      data-testid={dataTestId}
    >
      <div ref={ref} data-type={type} data-id={id}>
        {children}
        <div style={{ height: additionalHeight }} />
      </div>
    </div>
  )
}

/** To sync up heights for the same HeightDiv types. */
export default function HeightProvider({ children }: { children: ReactNode }): JSX.Element {
  const [compareHeights, setCompareHeights] = useState<Heights>({})
  const skipDelayIdsRef = useRef<Set<string>>(new Set())
  // Multiple mounted HeightDivs share an id (one per compare type), so ref-count before
  // removing the id from the set — otherwise one instance's unmount strips skipDelay from the rest
  const skipDelayCountsRef = useRef<Map<string, number>>(new Map())

  const processedHeights = useProcessedHeights(compareHeights, {
    debounceMs: 50,
    skipDelayIdsRef,
  })

  const maxHeights = useMemo(
    () =>
      Object.values(processedHeights).reduce<HeightById>((accum, value) => {
        if (value) {
          Object.entries(value).forEach(([id, height]) => {
            const currentHeight = accum[id]
            if ((currentHeight === undefined || currentHeight < height) && height !== undefined) accum[id] = height
          })
        }
        return accum
      }, {}),

    [processedHeights]
  )

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const { type, id } = (entry.target as HTMLElement).dataset
          const height = entry.contentRect.height

          if (type && id) {
            setCompareHeights((prev) => {
              const newState = { ...prev }
              newState[type] ??= {}
              newState[type][id] = height
              return newState
            })
          }
        })
      }),
    []
  )

  useEffect(() => {
    return () => {
      resizeObserver.disconnect()
    }
  }, [resizeObserver])

  const observe = useCallback(
    ({ element, id, skipDelay }: ObserveProps): void => {
      resizeObserver.observe(element)
      if (skipDelay) {
        skipDelayCountsRef.current.set(id, (skipDelayCountsRef.current.get(id) ?? 0) + 1)
        skipDelayIdsRef.current.add(id)
      }
    },
    [resizeObserver]
  )

  const unobserve = useCallback(
    ({ element, id, skipDelay }: ObserveProps): void => {
      resizeObserver.unobserve(element)
      if (skipDelay) {
        const remaining = (skipDelayCountsRef.current.get(id) ?? 0) - 1
        if (remaining > 0) {
          skipDelayCountsRef.current.set(id, remaining)
        } else {
          skipDelayCountsRef.current.delete(id)
          skipDelayIdsRef.current.delete(id)
        }
      }
    },
    [resizeObserver]
  )

  const value = useMemo(
    () => ({
      maxHeights,
      observe,
      unobserve,
    }),
    [maxHeights, observe, unobserve]
  )

  return <HeightContext.Provider value={value}>{children}</HeightContext.Provider>
}
